const API_URL = process.env.REACT_APP_WORDPRESS_API_URL || null;

async function fetchApi(endpoint, fallback = null) {
  if (!API_URL) {
    if (fallback !== null) return fallback;
    throw new Error("REACT_APP_WORDPRESS_API_URL no está configurada");
  }

  const url = `${API_URL}/wp-json${endpoint}`;
  const res = await fetch(url);

  if (!res.ok) {
    throw new Error(`Error ${res.status}: ${res.statusText}`);
  }

  return res.json();
}

function extractAcf(post) {
  const acf = post.acf || {};

  if (post.featured_image) {
    acf.imagen_destacada = post.featured_image;
  }

  return acf;
}

export async function getPersonajes() {
  const data = await fetchApi("/wp/v2/personajes?_embed&orderby=menu_order&order=asc&per_page=50", []);
  if (Array.isArray(data)) {
    return data.map((p) => ({
      id: p.id,
      nombre: p.title?.rendered || "",
      descripcion: p.content?.rendered || "",
      imagen: p.featured_image || extractAcf(p).personaje_imagen_card || null,
      orden: extractAcf(p).personaje_orden || 1,
      acf: extractAcf(p),
    }));
  }
  return data;
}

export async function getEventos() {
  const data = await fetchApi("/wp/v2/eventos?_embed&per_page=50", []);
  if (Array.isArray(data)) {
    return data.map((e) => ({
      id: e.id,
      titulo: e.title?.rendered || "",
      descripcion: e.content?.rendered || "",
      imagen: e.featured_image || extractAcf(e).evento_imagen_banner || null,
      fecha: extractAcf(e).evento_fecha || null,
      lugar: extractAcf(e).evento_lugar || null,
      hora: extractAcf(e).evento_hora || null,
      destacado: !!extractAcf(e).evento_destacado,
      actividades: extractAcf(e).evento_actividades || [],
      acf: extractAcf(e),
    }));
  }
  return data;
}

export async function getEventoById(id) {
  const e = await fetchApi(`/wp/v2/eventos/${id}?_embed`, null);
  if (!e) return null;
  return {
    id: e.id,
    titulo: e.title?.rendered || "",
    descripcion: e.content?.rendered || "",
    imagen: e.featured_image || extractAcf(e).evento_imagen_banner || null,
    fecha: extractAcf(e).evento_fecha || null,
    lugar: extractAcf(e).evento_lugar || null,
    hora: extractAcf(e).evento_hora || null,
    destacado: !!extractAcf(e).evento_destacado,
    actividades: extractAcf(e).evento_actividades || [],
    acf: extractAcf(e),
  };
}

export async function getActividades(categoria = null) {
  let endpoint = "/wp/v2/actividades?_embed&per_page=50";
  if (categoria) {
    endpoint += `&categoria_actividad=${encodeURIComponent(categoria)}`;
  }
  const data = await fetchApi(endpoint, []);
  if (Array.isArray(data)) {
    return data.map((a) => ({
      id: a.id,
      titulo: a.title?.rendered || "",
      descripcion: a.content?.rendered || "",
      imagen: a.featured_image || extractAcf(a).actividad_imagen_card || null,
      categoria: extractAcf(a).actividad_categoria || null,
      archivo: extractAcf(a).actividad_archivo_descargable || null,
      imagenHero: extractAcf(a).actividad_imagen_hero || null,
      imagenMateriales: extractAcf(a).actividad_imagen_materiales || null,
      materiales: extractAcf(a).actividad_materiales || [],
      destacado: !!extractAcf(a).actividad_destacada,
      acf: extractAcf(a),
    }));
  }
  return data;
}

export async function getActividadById(id) {
  const a = await fetchApi(`/wp/v2/actividades/${id}?_embed`, null);
  if (!a) return null;
  return {
    id: a.id,
    titulo: a.title?.rendered || "",
    descripcion: a.content?.rendered || "",
    imagen: a.featured_image || extractAcf(a).actividad_imagen_card || null,
    categoria: extractAcf(a).actividad_categoria || null,
    archivo: extractAcf(a).actividad_archivo_descargable || null,
    imagenHero: extractAcf(a).actividad_imagen_hero || null,
    imagenMateriales: extractAcf(a).actividad_imagen_materiales || null,
    materiales: extractAcf(a).actividad_materiales || [],
    destacado: !!extractAcf(a).actividad_destacada,
    acf: extractAcf(a),
  };
}

export async function getSlidesCarrusel() {
  const data = await fetchApi("/wp/v2/slide_carrusel?_embed&orderby=meta_value_num&meta_key=slide_orden&order=asc&per_page=10", []);
  if (Array.isArray(data)) {
    return data.map((s) => ({
      id: s.id,
      imagen: extractAcf(s).slide_imagen || s.featured_image || null,
      textoBoton: extractAcf(s).slide_texto_boton || "Conoce más",
      urlBoton: extractAcf(s).slide_url_boton || "#",
      colorBoton: extractAcf(s).slide_color_boton || "blue",
      orden: extractAcf(s).slide_orden || 1,
      acf: extractAcf(s),
    }));
  }
  return data;
}

export async function getHomeData() {
  return fetchApi("/balloon-ben/v1/home", {
    carrusel: [],
    secciones: [],
    eventos_destacados: [],
    actividades_destacadas: [],
    personajes: [],
  });
}

export async function getConfigPagina(slug) {
  return fetchApi(`/balloon-ben/v1/pagina/${slug}`, {
    slug,
    titulo: null,
    subtitulo: null,
    imagen: null,
    contenido_extra: null,
    playlist_youtube_id: null,
    lista_titulo: null,
    lista_subtitulo: null,
    eventos_info_titulo: null,
    eventos_info_texto: null,
    eventos_diferenciadores: [],
    eventos_frase_final: null,
    videos_descripciones: [],
  });
}

export async function getPoliticas() {
  return fetchApi("/balloon-ben/v1/politicas", []);
}

export async function getPoliticaBySlug(slug) {
  return fetchApi(`/balloon-ben/v1/politica/${slug}`, {
    slug,
    titulo: null,
    contenido: null,
    fecha_actualizacion: null,
  });
}

// Aplica las descripciones manuales (cargadas en WordPress) a los videos traídos de YouTube,
// que solo aportan miniatura y título. Se emparejan por video_id.
export function mergeVideoDescripciones(videos, descripcionesConfig = []) {
  if (!Array.isArray(descripcionesConfig) || descripcionesConfig.length === 0) {
    return videos;
  }
  const mapa = new Map(
    descripcionesConfig
      .filter((d) => d?.video_id)
      .map((d) => [d.video_id.trim(), d.video_descripcion || ""])
  );
  return videos.map((v) => ({
    ...v,
    descripcion: mapa.get(v.videoId) ?? v.descripcion ?? "",
  }));
}

export function isApiConfigured() {
  return !!API_URL;
}

export async function getProductos(categoria = null) {
  let endpoint = '/balloon-ben/v1/productos?per_page=50';
  if (categoria) {
    endpoint += `&categoria=${encodeURIComponent(categoria)}`;
  }
  const data = await fetchApi(endpoint, { productos: [], total: 0, woocommerce_activo: false });
  return Array.isArray(data?.productos) ? data.productos : [];
}

export async function getCategoriasProductos() {
  const data = await fetchApi('/balloon-ben/v1/productos/categorias', []);
  return Array.isArray(data) ? data : [];
}

export async function getPlaylistVideos(playlistId, maxResults = 6) {
  const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY;
  if (!apiKey || !playlistId) return [];
  try {
    const url = `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&playlistId=${encodeURIComponent(playlistId)}&maxResults=${maxResults}&key=${apiKey}`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    return (data.items || []).map((item) => ({
      videoId: item.snippet.resourceId.videoId,
      title: item.snippet.title,
      thumbnail: item.snippet.thumbnails?.high?.url || item.snippet.thumbnails?.medium?.url || "",
      // La descripción no viene de YouTube: se asigna manualmente en WordPress (ver mergeVideoDescripciones).
      descripcion: "",
    }));
  } catch {
    return [];
  }
}
